import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { ui } from "../../../components/ui";
import { Button } from "../../../components/ui/button";
import {
  getTenant,
  createTenant,
  updateTenant,
  SHARED_DB_HOST,
  DRIVER_CLASS } from
"../../../services/tenantService";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "../../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../../components/ui/popover";
import { cn } from "../../../lib/utils";

const EMPTY_FORM = {
  tenantName: "",
  tenantLogo: "",
  dbUsername: "",
  dbPassword: "",
  dbSchema: "",
  dbHost: SHARED_DB_HOST,
  isActive: true,
  maximumPoolSize: 10,
  minimumIdle: 2,
  connectionTimeout: 30,
  idleTimeout: 600,
  maxLifetime: 1800,
  leakDetectionThreshold: 0,
  subscriptionStartDate: "",
  subscriptionEndDate: ""
};

const inputClass =
"w-full h-10 rounded-xl border border-[var(--color-text-border)] px-3.5 text-sm text-foreground bg-white focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10 transition";
const inputErrorClass = "border-[var(--color-error)] focus:border-[var(--color-error)]";
const labelClass = "block text-sm font-semibold text-foreground mb-1.5";

// The API only returns a combined tenantDbUrl, e.g.
// "jdbc:postgresql://your-db-host:5432/1" — there's no separate dbHost
// field. Pull the host back out of that URL so the DB Host input can be
// pre-filled when editing an existing tenant.
function extractDbHostFromUrl(tenantDbUrl) {
  if (!tenantDbUrl) return "";
  const match = /^jdbc:postgresql:\/\/([^/]+)\//.exec(tenantDbUrl);
  return match ? match[1] : "";
}

function CreateEditTenantPage({ tenantId }) {
  const navigate = useNavigate();
  const isEditMode = Boolean(tenantId);

  const [form, setForm] = useState(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    if (!isEditMode) return;
    let cancelled = false;

    async function loadTenant() {
      setLoading(true);
      setBanner(null);
      try {
        const tenant = await getTenant(tenantId);
        if (cancelled) return;
        setForm({
          tenantName: tenant.tenantName || "",
          tenantLogo: tenant.tenantLogo || "",
          dbUsername: tenant.dbUsername || "",
          dbPassword: tenant.dbPassword || "",
          dbSchema: tenant.dbSchema || "",
          dbHost: tenant.dbHost || extractDbHostFromUrl(tenant.tenantDbUrl) || SHARED_DB_HOST,
          isActive: tenant.isActive !== undefined ? tenant.isActive : true,
          maximumPoolSize: tenant.maximumPoolSize ?? 10,
          minimumIdle: tenant.minimumIdle ?? 2,
          connectionTimeout: tenant.connectionTimeout ?? 30,
          idleTimeout: tenant.idleTimeout ?? 600,
          maxLifetime: tenant.maxLifetime ?? 1800,
          leakDetectionThreshold: tenant.leakDetectionThreshold ?? 0,
          subscriptionStartDate: tenant.subscriptionStartDate || "",
          subscriptionEndDate: tenant.subscriptionEndDate || ""
        });
      } catch (err) {
        if (!cancelled) setBanner("Couldn't load this tenant: " + err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTenant();
    return () => {
      cancelled = true;
    };
  }, [tenantId, isEditMode]);

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function validate() {
    const errors = {};
    if (!form.tenantName.trim()) errors.tenantName = "Name is required.";
    if (!form.dbUsername.trim()) errors.dbUsername = "DB Username is required.";
    if (!form.dbPassword.trim()) errors.dbPassword = "DB Password is required.";
    if (!form.dbSchema.trim()) errors.dbSchema = "DB Schema is required.";
    if (!form.dbHost.trim()) errors.dbHost = "DB Host is required.";
    if (!form.subscriptionStartDate) errors.subscriptionStartDate = "Start date is required.";
    if (!form.subscriptionEndDate) errors.subscriptionEndDate = "End date is required.";
    if (
    form.subscriptionStartDate &&
    form.subscriptionEndDate &&
    form.subscriptionEndDate < form.subscriptionStartDate)
    {
      errors.subscriptionEndDate = "End date must be on or after the start date.";
    }
    if (
    !Number.isInteger(Number(form.maximumPoolSize)) ||
    form.maximumPoolSize < 1 ||
    form.maximumPoolSize > 999)
    {
      errors.maximumPoolSize = "Enter a whole number between 1 and 999.";
    }
    for (const key of ["minimumIdle", "connectionTimeout", "idleTimeout", "maxLifetime", "leakDetectionThreshold"]) {
      if (!Number.isInteger(Number(form[key])) || Number(form[key]) < 0) {
        errors[key] = "Enter a non-negative whole number.";
      }
    }
    return errors;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    setBanner(null);
    try {
      const payload = {
        ...form,
        maximumPoolSize: parseInt(form.maximumPoolSize, 10),
        minimumIdle: parseInt(form.minimumIdle, 10),
        connectionTimeout: parseInt(form.connectionTimeout, 10),
        idleTimeout: parseInt(form.idleTimeout, 10),
        maxLifetime: parseInt(form.maxLifetime, 10),
        leakDetectionThreshold: parseInt(form.leakDetectionThreshold, 10),
        tenantDbUrl: `jdbc:postgresql://${form.dbHost}/${form.dbSchema}`,
        driverClass: DRIVER_CLASS
      };

      if (isEditMode) {
        await updateTenant(tenantId, payload);
      } else {
        await createTenant(payload);
      }
      navigate("/hrms-team/config/tenants");
    } catch (err) {
      setBanner("Couldn't save this tenant: " + err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppLayout>
      <div className="mb-5">
        <PageBackLink to="/psm/config/tenants" />
        <h1 className={ui.pageTitle}>
          {isEditMode ? "Edit Tenant" : "Create Tenant"}
        </h1>
      </div>

      <div className="bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border border-white/60 max-w-[860px]">
        {loading ?
        <div className="px-6 py-16 text-center text-sm text-[var(--color-text-secondary)]"><Trans i18nKey="tenantManagement.loadingTenant" /></div> :

        <form onSubmit={handleSubmit} className="px-5 sm:px-8 py-6 sm:py-8">
            {banner &&
          <div className="mb-6 p-3 sm:p-4 rounded-xl border border-[var(--color-error)]/25 bg-[var(--color-error)]/10 text-sm font-semibold text-[var(--color-error)]">
                {banner}
              </div>
          }

            <p className="text-[15px] font-bold text-foreground mt-0 mb-4 pb-2 border-b border-[var(--color-text-border)]/70">
              <Trans i18nKey="tenantManagement.tenantIdentity" />
            </p>

            <div className="mb-5">
              <label className={labelClass}>
                <Trans i18nKey="common.name" /><span className="text-[var(--color-error)]">*</span>
              </label>
              <input
              type="text"
              className={`${inputClass} ${fieldErrors.tenantName ? inputErrorClass : ""}`}
              value={form.tenantName}
              onChange={(e) => updateField("tenantName", e.target.value)}
              placeholder={i18n.t("examples.eGSurakshaFireServices")} />
            
              {fieldErrors.tenantName &&
            <p className="text-[var(--color-error)] text-xs mt-1">{fieldErrors.tenantName}</p>
            }
            </div>

         <div className="mb-5">
           <label className={labelClass}><Trans i18nKey="tenantManagement.companyLogoUrl" /></label>
           <input
              type="text"
              className={inputClass}
              value={form.tenantLogo}
              onChange={(e) => updateField("tenantLogo", e.target.value)}
              placeholder="https://example.com/logo.png" />
            
           <div className="flex items-center gap-3 mt-2">
             {form.tenantLogo ?
              <img
                src={form.tenantLogo}
                alt={i18n.t("tenantManagement.logoPreview")}
                className="h-11 w-11 rounded-lg object-cover border border-[var(--color-text-border)]"
                onError={(e) => {
                  e.target.style.visibility = "hidden";
                }} /> :


              <div className="h-11 w-11 rounded-lg bg-[var(--color-workspace)]/60 border border-[var(--color-text-border)]" />
              }
           </div>
         </div>

            <div className="flex items-center gap-2.5 mb-5">
              <button
              type="button"
              onClick={() => updateField("isActive", !form.isActive)}
              className={`w-11 h-6 rounded-full relative shrink-0 transition-colors ${
              form.isActive ? "bg-[var(--color-success)]" : "bg-[var(--color-text-border)]"}`
              }>
              
                <span
                className={`absolute top-0.5 h-[18px] w-[18px] rounded-full bg-white transition-all ${
                form.isActive ? "left-[23px]" : "left-[3px]"}`
                } />
              
              </button>
              <label
              className="text-sm font-semibold text-foreground cursor-pointer"
              onClick={() => updateField("isActive", !form.isActive)}>
                <Trans i18nKey="tenantManagement.tenantActive" />
              
            </label>
            </div>

            <p className="text-[15px] font-bold text-foreground mt-8 mb-4 pb-2 border-b border-[var(--color-text-border)]/70">
              <Trans i18nKey="tenantManagement.databaseConnection" />
            </p>

            <div className="mb-5">
              <label className={labelClass}>
                <Trans i18nKey="tenantManagement.dbUsername" /><span className="text-[var(--color-error)]">*</span>
              </label>
              <input
              type="text"
              className={`${inputClass} ${fieldErrors.dbUsername ? inputErrorClass : ""}`}
              value={form.dbUsername}
              onChange={(e) => updateField("dbUsername", e.target.value)} />
            
              {fieldErrors.dbUsername &&
            <p className="text-[var(--color-error)] text-xs mt-1">{fieldErrors.dbUsername}</p>
            }
            </div>

            <div className="mb-5">
              <label className={labelClass}>
                <Trans i18nKey="tenantManagement.dbPassword" /><span className="text-[var(--color-error)]">*</span>
              </label>
              <div className="relative">
                <input
                type={showPassword ? "text" : "password"}
                className={`${inputClass} pr-16 ${fieldErrors.dbPassword ? inputErrorClass : ""}`}
                value={form.dbPassword}
                onChange={(e) => updateField("dbPassword", e.target.value)} />
              
                <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-[var(--color-accent)]"
                onClick={() => setShowPassword((v) => !v)}>
                
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              {fieldErrors.dbPassword &&
            <p className="text-[var(--color-error)] text-xs mt-1">{fieldErrors.dbPassword}</p>
            }
            </div>

            <div className="mb-5">
              <label className={labelClass}>
                <Trans i18nKey="tenantManagement.dbSchema" /><span className="text-[var(--color-error)]">*</span>
              </label>
              <input
              type="text"
              className={`${inputClass} ${fieldErrors.dbSchema ? inputErrorClass : ""}`}
              value={form.dbSchema}
              onChange={(e) => updateField("dbSchema", e.target.value)}
              placeholder={i18n.t("examples.eGSurakshaFireDb")} />
            
              {fieldErrors.dbSchema &&
            <p className="text-[var(--color-error)] text-xs mt-1">{fieldErrors.dbSchema}</p>
            }
            </div>

            <div className="mb-5">
              <label className={labelClass}>
                <Trans i18nKey="tenantManagement.dbHost" /><span className="text-[var(--color-error)]">*</span>
              </label>
              <input
              type="text"
              className={`${inputClass} ${fieldErrors.dbHost ? inputErrorClass : ""}`}
              value={form.dbHost}
              onChange={(e) => updateField("dbHost", e.target.value)}
              placeholder={i18n.t("examples.eGYourDbHost5432")} />
            
              {fieldErrors.dbHost &&
            <p className="text-[var(--color-error)] text-xs mt-1">{fieldErrors.dbHost}</p>
            }
            </div>

            <p className="text-[15px] font-bold text-foreground mt-8 mb-4 pb-2 border-b border-[var(--color-text-border)]/70">
              <Trans i18nKey="tenantManagement.connectionPool" />
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-1">
              <div className="mb-4">
                <label className={labelClass}><Trans i18nKey="tenantManagement.maxPoolSize" /></label>
                <input
                type="text"
                inputMode="numeric"
                maxLength={3}
                className={`${inputClass} ${fieldErrors.maximumPoolSize ? inputErrorClass : ""}`}
                value={form.maximumPoolSize}
                onChange={(e) => updateField("maximumPoolSize", e.target.value.replace(/\D/g, ""))} />
              
                {fieldErrors.maximumPoolSize &&
              <p className="text-[var(--color-error)] text-xs mt-1">{fieldErrors.maximumPoolSize}</p>
              }
              </div>
              <div className="mb-4">
                <label className={labelClass}><Trans i18nKey="validation.minimumIdle" /></label>
                <input
                type="text"
                inputMode="numeric"
                className={`${inputClass} ${fieldErrors.minimumIdle ? inputErrorClass : ""}`}
                value={form.minimumIdle}
                onChange={(e) => updateField("minimumIdle", e.target.value.replace(/\D/g, ""))} />
              
                {fieldErrors.minimumIdle &&
              <p className="text-[var(--color-error)] text-xs mt-1">{fieldErrors.minimumIdle}</p>
              }
              </div>
              <div className="mb-4">
                <label className={labelClass}><Trans i18nKey="dateTime.connectionTimeoutS" /></label>
                <input
                type="text"
                inputMode="numeric"
                className={`${inputClass} ${fieldErrors.connectionTimeout ? inputErrorClass : ""}`}
                value={form.connectionTimeout}
                onChange={(e) => updateField("connectionTimeout", e.target.value.replace(/\D/g, ""))} />
              
                {fieldErrors.connectionTimeout &&
              <p className="text-[var(--color-error)] text-xs mt-1">{fieldErrors.connectionTimeout}</p>
              }
              </div>
              <div className="mb-4">
                <label className={labelClass}><Trans i18nKey="dateTime.idleTimeoutS" /></label>
                <input
                type="text"
                inputMode="numeric"
                className={`${inputClass} ${fieldErrors.idleTimeout ? inputErrorClass : ""}`}
                value={form.idleTimeout}
                onChange={(e) => updateField("idleTimeout", e.target.value.replace(/\D/g, ""))} />
              
                {fieldErrors.idleTimeout &&
              <p className="text-[var(--color-error)] text-xs mt-1">{fieldErrors.idleTimeout}</p>
              }
              </div>
              <div className="mb-4">
                <label className={labelClass}><Trans i18nKey="dateTime.maxLifetimeS" /></label>
                <input
                type="text"
                inputMode="numeric"
                className={`${inputClass} ${fieldErrors.maxLifetime ? inputErrorClass : ""}`}
                value={form.maxLifetime}
                onChange={(e) => updateField("maxLifetime", e.target.value.replace(/\D/g, ""))} />
              
                {fieldErrors.maxLifetime &&
              <p className="text-[var(--color-error)] text-xs mt-1">{fieldErrors.maxLifetime}</p>
              }
              </div>
              <div className="mb-4">
                <label className={labelClass}><Trans i18nKey="tenantManagement.leakDetectionS" /></label>
                <input
                type="text"
                inputMode="numeric"
                className={`${inputClass} ${fieldErrors.leakDetectionThreshold ? inputErrorClass : ""}`}
                value={form.leakDetectionThreshold}
                onChange={(e) => updateField("leakDetectionThreshold", e.target.value.replace(/\D/g, ""))} />
              
                {fieldErrors.leakDetectionThreshold &&
              <p className="text-[var(--color-error)] text-xs mt-1">{fieldErrors.leakDetectionThreshold}</p>
              }
              </div>
            </div>

            <p className="text-[15px] font-bold text-foreground mt-2 mb-4 pb-2 border-b border-[var(--color-text-border)]/70">
              <Trans i18nKey="tenantManagement.membership" />
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className={labelClass}>
                  <Trans i18nKey="dateTime.startDate2" /><span className="text-[var(--color-error)]">*</span>
                </label>
               <Popover>
                           <PopoverTrigger asChild>
                                   <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      "w-full h-10 justify-start text-left font-normal rounded-xl border bg-white px-3 text-sm hover:bg-[var(--color-workspace)]/40",
                      !form.subscriptionStartDate && "text-muted-foreground",
                      fieldErrors.subscriptionStartDate ? "border-[var(--color-error)]" : "border-[var(--color-text-border)]"
                    )}>
                    
                                     <CalendarIcon className="mr-2 h-4 w-4 text-[var(--color-text-secondary)]" />
                                     {form.subscriptionStartDate ? format(new Date(form.subscriptionStartDate), "PPP") : <span><Trans i18nKey="dateTime.pickADate" /></span>}
                                   </Button>
                                 </PopoverTrigger>
                                 <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                                   <Calendar
                    mode="single"
                    selected={form.subscriptionStartDate ? new Date(form.subscriptionStartDate) : undefined}
                    onSelect={(date) => date && updateField("subscriptionStartDate", format(date, "yyyy-MM-dd"))}
                    initialFocus
                    className="p-3 pointer-events-auto" />
                  
                                 </PopoverContent>
                               </Popover>
                {fieldErrors.subscriptionStartDate &&
              <p className="text-[var(--color-error)] text-xs mt-1">{fieldErrors.subscriptionStartDate}</p>
              }
              </div>
              <div>
                <label className={labelClass}>
                  <Trans i18nKey="dateTime.endDate2" /><span className="text-[var(--color-error)]">*</span>
                </label>
                <Popover>
                                  <PopoverTrigger asChild>
                                    <Button
                    type="button"
                    variant="outline"
                    className={cn(
                      "w-full h-10 justify-start text-left font-normal rounded-xl border bg-white px-3 text-sm hover:bg-[var(--color-workspace)]/40",
                      !form.subscriptionEndDate && "text-muted-foreground",
                      fieldErrors.subscriptionEndDate ? "border-[var(--color-error)]" : "border-[var(--color-text-border)]"
                    )}>
                    
                                      <CalendarIcon className="mr-2 h-4 w-4 text-[var(--color-text-secondary)]" />
                                      {form.subscriptionEndDate ? format(new Date(form.subscriptionEndDate), "PPP") : <span><Trans i18nKey="dateTime.pickADate" /></span>}
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-auto border-0 bg-transparent p-0 shadow-none" align="start">
                                    <Calendar
                    mode="single"
                    selected={form.subscriptionEndDate ? new Date(form.subscriptionEndDate) : undefined}
                    onSelect={(date) => date && updateField("subscriptionEndDate", format(date, "yyyy-MM-dd"))}
                    initialFocus
                    className="p-3 pointer-events-auto" />
                  
                                  </PopoverContent>
                                </Popover>
                {fieldErrors.subscriptionEndDate &&
              <p className="text-[var(--color-error)] text-xs mt-1">{fieldErrors.subscriptionEndDate}</p>
              }
              </div>
            </div>

            <div className="flex gap-2.5 pt-5 border-t border-[var(--color-text-border)]/70">
              <Button
              type="submit"
              disabled={submitting}
              className="h-11 px-5 rounded-xl bg-[var(--color-primary)] text-white hover:bg-[var(--color-accent)] shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)]">
              
                {submitting ? "Saving…" : "Save"}
              </Button>
              <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/hrms-team/config/tenants")}
              className="h-11 px-5 rounded-xl">
                <Trans i18nKey="formActions.cancel" />
              
            </Button>
            </div>
          </form>
        }
      </div>
    </AppLayout>);

}

export function EditTenantRoutePage() {
  const { id } = useParams();
  return <CreateEditTenantPage tenantId={id} />;
}

export { CreateEditTenantPage };
